import axios from "axios";
import Sidebar from "../components/Sidebar";
import { useDateAndAdmin } from "../utils/dateUtils";
import { useEffect, useState, useRef, useCallback } from "react";
import { getAuthToken } from "../utils/cookieUtils";
import Pagination from "../components/pagination";

import EditLocationModal from "../components/modals/locationModal/EditLocationModal";
import CreateLocationModal from "../components/modals/locationModal/CreateLocationModal";
import LocationStatusModal from "../components/modals/locationModal/LocationStatusModal";
import DisableLocationModal from "../components/modals/locationModal/DisableLocationModal";
import EnableLocationModal from "../components/modals/locationModal/EnableLocationModal";
import { handleExportLocationsCSV, handleExportLocationsCSVWithFetch } from "../utils/csvExportLocations";


function Location() {
  
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const { formattedDate, adminData } = useDateAndAdmin();
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("");
  const [statusDropdownOpen, setStatusDropdownOpen] = useState(false);
  const [optionsPopupOpen, setOptionsPopupOpen] = useState(null);
  const [pagination, setPagination] = useState({
    current_page: 1,
    last_page: 1,
    per_page: 10,
    total: 0,
    from: 0,
    to: 0,
  });
  const popupRef = useRef(null);

  
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editLocation, setEditLocation] = useState(null);
  const [statusModalOpen, setStatusModalOpen] = useState(false);
  const [statusModalMsg, setStatusModalMsg] = useState({ title: '', description: '', buttonText: 'Cancel' });


  
  const [disableLocationModalOpen, setDisableLocationModalOpen] = useState(false);
  const [enableLocationModalOpen, setEnableLocationModalOpen] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState(null);

  
  const handleDisableLocation = (location) => {
    setSelectedLocation(location);
    setEnableLocationModalOpen(false); 
    setDisableLocationModalOpen(true);
    setOptionsPopupOpen(null);
  };
  
  const handleEnableLocation = (location) => {
    setSelectedLocation(location);
    setDisableLocationModalOpen(false); 
    setEnableLocationModalOpen(true);
    setOptionsPopupOpen(null);
  };


  
  const confirmDisableLocation = async () => {
    if (!selectedLocation) return;
    try {
      const token = getAuthToken();
      
      const form = {
        name: selectedLocation.name,
        address: selectedLocation.address,
        phone: selectedLocation.phone,
        status: "inactive"
      };
      const response = await axios.put(`/super-admin/locations/${selectedLocation.id}`, form, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setStatusModalMsg({
        title: "Location Disabled",
        description: "Location has been disabled successfully.",
        buttonText: "OK",
      });
      setStatusModalOpen(true);
      
      const updatedLoc = response?.data?.data;
      setLocations((prev) =>
        prev.map((loc) =>
          loc.id === selectedLocation.id
            ? (updatedLoc ? { ...loc, ...updatedLoc } : { ...loc, status: "inactive" })
            : loc
        )
      );
    } catch (err) {
      console.error("DISABLE error:", err);
      setStatusModalMsg({
        title: "Error",
        description: `Failed to disable location. ${err?.response?.data?.message || err.message}`,
        buttonText: "Close",
      });
      setStatusModalOpen(true);
    } finally {
      setDisableLocationModalOpen(false);
      setSelectedLocation(null);
    }
  };

  
  const confirmEnableLocation = async () => {
    if (!selectedLocation) return;
    try {
      const token = getAuthToken();
      
      const form = {
        name: selectedLocation.name,
        address: selectedLocation.address,
        phone: selectedLocation.phone,
        status: "active"
      };
      const response = await axios.put(`/super-admin/locations/${selectedLocation.id}`, form, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setStatusModalMsg({
        title: "Location Enabled",
        description: "Location has been enabled successfully.",
        buttonText: "OK",
      });
      setStatusModalOpen(true);
      
      const updatedLoc = response?.data?.data;
      setLocations((prev) =>
        prev.map((loc) =>
          loc.id === selectedLocation.id
            ? (updatedLoc ? { ...loc, ...updatedLoc } : { ...loc, status: "active" })
            : loc
        )
      );
    } catch (err) {
      console.error("ENABLE error:", err);
      setStatusModalMsg({
        title: "Error",
        description: `Failed to enable location. ${err?.response?.data?.message || err.message}`,
        buttonText: "Close",
      });
      setStatusModalOpen(true);
    } finally {
      setEnableLocationModalOpen(false);
      setSelectedLocation(null);
    }
  };

  
  const modals = (
    <>
      <DisableLocationModal
        isOpen={disableLocationModalOpen}
        handleClose={() => { setDisableLocationModalOpen(false); setSelectedLocation(null); }}
        location={selectedLocation}
        handleDisableLocation={confirmDisableLocation}
      />
      <EnableLocationModal
        isOpen={enableLocationModalOpen}
        handleClose={() => { setEnableLocationModalOpen(false); setSelectedLocation(null); }}
        location={selectedLocation}
        handleEnableLocation={confirmEnableLocation}
      />
    </>
  );

  
  const handleEditLocation = (location) => {
    setEditLocation(location);
    setEditModalOpen(true);
    setOptionsPopupOpen(null);
  };

  
  const handleSaveEditLocation = async (form) => {
    if (!editLocation) return;
    try {
      const token = getAuthToken();
      await axios.put(`/super-admin/locations/${editLocation.id}`, form, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setStatusModalMsg({
        title: "Location Updated",
        description: "Location details have been updated successfully.",
        buttonText: "OK",
      });
      setStatusModalOpen(true);
      
      setLocations((prev) => prev.map((loc) => loc.id === editLocation.id ? { ...loc, ...form } : loc));
      setEditModalOpen(false);
    } catch (err) {
      setStatusModalMsg({
        title: "Error",
        description: "Failed to update location. Please try again.",
        buttonText: "Close",
      });
      setStatusModalOpen(true);
    }
  };

  
  const handleCreateLocation = () => {
    setCreateModalOpen(true);
  };

  const handleSaveCreateLocation = async (form) => {
    try {
      const token = getAuthToken();
      const res = await axios.post("/super-admin/locations", form, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setStatusModalMsg({
        title: "Location Created",
        description: "Location has been created successfully.",
        buttonText: "OK",
      });
      setStatusModalOpen(true);
      
      const newLoc = res.data?.data || form;
      setLocations((prev) => [...prev, newLoc]);
      setCreateModalOpen(false);
    } catch (err) {
      setStatusModalMsg({
        title: "Error",
        description: "Failed to create location. Please try again.",
        buttonText: "Close",
      });
      setStatusModalOpen(true);
    }
  };

  
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (popupRef.current && !popupRef.current.contains(event.target)) {
        setOptionsPopupOpen(null);
      }
      // Close dropdown when clicking outside
      if (statusDropdownOpen && !event.target.closest('.status-filter')) {
        setStatusDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [popupRef, statusDropdownOpen]);

  const fetchLocations = useCallback(async (page = 1, locationName = "", status = "") => {
    setLoading(true);
    try {
      const token = getAuthToken();
      const params = { page };
      if (locationName && locationName.trim()) {
        params.name = locationName.trim();
      }
      
      if (status) {
        params.status = status;
      }
      
      const response = await axios.get("/super-admin/locations", {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params,
      });
      
      const responseData = response.data?.data || {};
      const locationsArr = responseData.data || [];
      
      setLocations(locationsArr);
      setPagination({
        current_page: responseData.current_page || 1,
        last_page: responseData.last_page || 1,
        per_page: responseData.per_page || 10,
        total: responseData.total || 0,
        from: responseData.from || 0,
        to: responseData.to || 0,
      });
    } catch (error) {
      console.error("Error fetching locations:", error);
    } finally {
      setLoading(false);
    }
  }, []);


useEffect(() => {
  const timeoutId = setTimeout(() => {
    fetchLocations(pagination.current_page, searchTerm, selectedStatus);
  }, 300);

  return () => clearTimeout(timeoutId);
}, [pagination.current_page, searchTerm, selectedStatus, fetchLocations]);


  const handleSearch = (e) => {
    const value = e.target.value;
    setSearchTerm(value);    if (pagination.current_page !== 1) {
      setPagination(prev => ({ ...prev, current_page: 1 }));
    }
  };

  const handleStatusFilter = (status) => {
    setSelectedStatus(status);
    setStatusDropdownOpen(false);
    if (pagination.current_page !== 1) {
      setPagination(prev => ({ ...prev, current_page: 1 }));
    }
  };

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, current_page: newPage }));
  };

  return (
    <>
      {modals}
      <div className="dashboard">
        <div className="Nav">
          <Sidebar />
        </div>

        <div className="dashboard-display">
          <div className="top-bar container-fluid">
            <div className="top-bar-info">
              <p id="date">{formattedDate}</p>
              <b>
                <p>Welcome back {adminData}</p>
              </b>
            </div>

            <div className="top-bar-button">
              <button onClick={handleCreateLocation}>
                <img
                  src={`${process.env.PUBLIC_URL}/images/add.png`}
                  alt="Add location"
                />{" "}
                Add New Location
              </button>
            </div>
          </div>
          <div className="main-dashboard container-fluid sub-dashboard">
            <div
              style={{ padding: "24px", paddingBottom: "0", paddingTop: "0" }}
              className="main-settings staff-items"
            >
              <div className="search">
                <input
                  type="text"
                  placeholder="Search by location name..."
                  value={searchTerm}
                  onChange={handleSearch}
                />
              </div>
              <div className="export-group">
                {/* Status Filter */}
                <div className="status-filter">
                  <button
                    className={`status-filter-button ${selectedStatus ? 'active' : ''}`}
                    onClick={() => setStatusDropdownOpen(!statusDropdownOpen)}
                  >
                    <span>
                      {selectedStatus === "active" ? "Active" 
                       : selectedStatus === "inactive" ? "Inactive"
                       : "All Status"
                      }
                    </span>
                    <span className={`status-filter-arrow ${statusDropdownOpen ? 'open' : ''}`}>
                      <svg className="filter-arrow-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
                        <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </span>
                  </button>
                  {statusDropdownOpen && (
                    <div className="status-filter-dropdown">
                      <div
                        className={`status-filter-option ${!selectedStatus ? 'selected' : ''}`}
                        onClick={() => handleStatusFilter("")}
                      >
                        All Status
                      </div>
                      <div
                        className={`status-filter-option ${selectedStatus === "active" ? 'selected' : ''}`}
                        onClick={() => handleStatusFilter("active")}
                      >
                        Active
                      </div>
                      <div
                        className={`status-filter-option ${selectedStatus === "inactive" ? 'selected' : ''}`}
                        onClick={() => handleStatusFilter("inactive")}
                      >
                        Inactive
                      </div>
                    </div>
                  )}
                </div>

                <div className="export">
                  <button
                    onClick={() => handleExportLocationsCSVWithFetch(axios.create({
                      baseURL: process.env.REACT_APP_API_BASE_URL || '',
                      headers: { Authorization: `Bearer ${getAuthToken()}` },
                    }), searchTerm, selectedStatus)}
                  >
                    <img
                      src={`${process.env.PUBLIC_URL}/images/export-icon.png`}
                      alt="Export"
                    />{" "}
                    Export all
                  </button>
                </div>
                <div className="export">
                  <button
                    onClick={() => {
                      handleExportLocationsCSV(locations);
                    }}
                  >
                    <img
                      src={`${process.env.PUBLIC_URL}/images/export-icon.png`}
                      alt="Export"
                    />{" "}
                    Export Page
                  </button>
                </div>
              </div>
            </div>
            <div style={{ marginTop: "0" }} className="show-admin-table">
              <table className="admin-table member-table">
                <thead>
                  <tr>
                    <th>S/N</th>
                    <th>Location</th>
                    <th>Address</th>
                    <th>Phone Number</th>
                    <th>Status</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan="6">
                        <div className="spinner"></div>
                      </td>
                    </tr>
                  ) : locations.length === 0 ? (
                    <tr>
                      <td colSpan="6" style={{ textAlign: "center" }}>No locations found.</td>
                    </tr>
                  ) : (
                    locations.map((loc) => (
                      <tr key={loc.id}>
                        <td>{loc.id}</td>
                        <td>{loc.name || '-'}</td>
                        <td>{loc.address || '-'}</td>
                        <td>{loc.phone || '-'}</td>
                        <td style={{ textTransform: 'capitalize' }}>{loc.status || '-'}</td>
                        <td>
                            <div className="options-menu">
                              <img
                                src={`${process.env.PUBLIC_URL}/images/horizontal-menu.png`}
                                onClick={() => setOptionsPopupOpen(loc.id)}
                                alt="Options menu"
                                style={{ cursor: 'pointer' }}
                              />
                              {optionsPopupOpen === loc.id && (
                                <div className="popup" ref={popupRef} style={{zIndex: 10}}>
                                  <ul style={{minWidth: '120px'}}>
                                    <li
                                      onClick={() => handleEditLocation(loc)}
                                      style={{cursor: 'pointer' }}
                                    >
                                      <img
                                        src={`${process.env.PUBLIC_URL}/images/staff-edit.png`}
                                        alt="Edit"
                                      />
                                      Edit Location
                                    </li>
                                    {loc.status === 'active' ? (
                                      <li
                                        onClick={() => handleDisableLocation(loc)}
                                        style={{ color: '#DD9828', cursor: 'pointer' }}
                                      >
                                        <img
                                          src={`${process.env.PUBLIC_URL}/images/disable-user.png`}
                                          alt="Disable"
                                        />
                                        Disable
                                      </li>
                                    ) : (
                                      <li
                                        onClick={() => handleEnableLocation(loc)}
                                        style={{ color: '#0473BD', cursor: 'pointer' }}
                                      >
                                        <img
                                          src={`${process.env.PUBLIC_URL}/images/enable-user.png`}
                                          alt="Enable"
                                        />
                                        Enable
                                      </li>
                                    )}
                                  </ul>
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))
                  )}
                </tbody>
              </table>

              <Pagination
                currentPageProp={pagination.current_page}
                totalPageProp={pagination.last_page}
                onPageChange={handlePageChange}
                loading={loading}
              />
            </div>
          </div>
        </div>
      </div>
      <EditLocationModal
        isOpen={editModalOpen}
        onRequestClose={() => setEditModalOpen(false)}
        location={editLocation}
        onSave={handleSaveEditLocation}
      />
      <CreateLocationModal
        isOpen={createModalOpen}
        onRequestClose={() => setCreateModalOpen(false)}
        onSave={handleSaveCreateLocation}
      />
      <LocationStatusModal
        isOpen={statusModalOpen}
        onRequestClose={() => setStatusModalOpen(false)}
        title={statusModalMsg.title}
        description={statusModalMsg.description}
        buttonText={statusModalMsg.buttonText}
      />
    </>
  );
}

export default Location;
